import { Flex, Grid, Image, ListItem, Text, UnorderedList } from '@chakra-ui/react'
import IconeAdaptativo from './assets/adaptativo.png'
import IconeAncoragem from './assets/ancoragem.png'
import IconeApoio from './assets/apoio.png'
import IconeCultivo from './assets/cultivo.png'
import IconeDirecionamento from './assets/direcionamento.png'
import IconeEstabilidade from './assets/estabilidade.png'
import IconeExecucao from './assets/execucao.png'
import IconeExploracao from './assets/exploracao.png'
import IconeProducao from './assets/producao.png'

interface iProps {
  value?: string
  onChange: (cultura?: string) => void
}

interface iCultura {
  nome: string
  icone: string
  perfil: string
  posicionamento: string
  descricao: string
  pontosFortes: string[]
  pontosAtencao: string[]
}

// Ordem da grade segue o modelo de Cameron e Quinn: flexibilidade em cima, controle embaixo, foco interno à esquerda e externo à direita
const culturas: iCultura[] = [
  {
    nome: 'Cultivo',
    icone: IconeCultivo,
    perfil: 'Participativos, colaborativos, acessíveis',
    posicionamento: 'Flexibilidade e foco interno',
    descricao: 'Cultura voltada às pessoas, à cooperação e à lealdade entre os membros da equipe. Corresponde ao quadrante Grupo/Colaboração do modelo de Cameron e Quinn: a empresa valoriza o moral, a coesão e o desenvolvimento das pessoas, em um ambiente amigável e solidário, com lideranças que atuam como facilitadoras e mentoras. As decisões consideram o bem-estar dos colaboradores e são tomadas preferencialmente por consenso.',
    pontosFortes: [
      'Tendem a ser amigáveis, acolhedores e cooperativos',
      'Buscam evitar conflitos e, quando surgem, procuram lidar de forma construtiva',
      'Valorizam o trabalho em equipe e a colaboração, sempre com foco no bem-estar dos colegas',
      'Priorizam oferecer às pessoas as condições e oportunidades necessárias para que tenham sucesso em suas funções'
    ],
    pontosAtencao: [
      'Podem ser mais cautelosos com riscos, agravados pela busca pelo consenso',
      'Tendem a ter dificuldade em lidar com pressão, podendo demorar para se ajustar ou se adaptar a mudanças',
      'Em alguns casos, podem não dar a devida atenção a resultados, entregas e desempenho',
      'Têm dificuldade em tomar decisões difíceis, especialmente quando elas podem não agradar a todos'
    ]
  },
  {
    nome: 'Apoio',
    icone: IconeApoio,
    perfil: 'Sociáveis, animados, abertos a novas ideias',
    posicionamento: 'Flexibilidade, entre o foco interno e o externo',
    descricao: 'Cultura sociável e cheia de energia, que combina o foco nas pessoas da cultura de Cultivo com a abertura à inovação da cultura de Exploração. O engajamento de colaboradores e clientes está sempre em evidência, as ideias são construídas em conjunto e a comunicação tende a ser informal. Os conflitos são resolvidos buscando o conforto de todos os envolvidos.',
    pontosFortes: [
      'Valorizam as ideias do time e gostam de explorar novas possibilidades em conjunto',
      'Buscam animar e incentivar uns aos outros, fortalecendo os relacionamentos e a confiança mútua',
      'Têm postura proativa para ajudar e compartilhar informações, mantendo uma comunicação mais informal',
      'Procuram resolver conflitos de forma que todos se sintam confortáveis, mas valorizam discussões construtivas'
    ],
    pontosAtencao: [
      'Podem encontrar dificuldade ao equilibrar as necessidades internas das pessoas com as demandas por resultados',
      'A falta de planejamento e controle sobre ações e processos pode comprometer a execução',
      'A busca por conforto e envolvimento de todos pode reduzir a agilidade nas decisões e na implementação',
      'Pouco foco em processos e eficiência pode gerar desalinhamentos e duplicação de esforços'
    ]
  },
  {
    nome: 'Exploração',
    icone: IconeExploracao,
    perfil: 'Ousados, tolerantes a risco, inovadores',
    posicionamento: 'Flexibilidade e foco externo',
    descricao: 'Cultura ousada, imaginativa e disposta a correr riscos. Corresponde ao quadrante Inovação/Flexibilidade do modelo de Cameron e Quinn: a empresa valoriza a mudança, a evolução e o aprendizado por tentativa e erro, em um ambiente dinâmico e criativo, com lideranças inovadoras, empreendedoras e visionárias. Prioriza agilidade, criatividade nas soluções e espírito empreendedor.',
    pontosFortes: [
      'Têm visão ampla do todo e compartilham ideias de forma mais aberta e informal',
      'São ambiciosos e motivados, impulsionam o progresso com energia e entusiasmo',
      'Demonstram curiosidade e disposição para aprender e experimentar o novo',
      'Adaptam-se com agilidade às mudanças e mantêm bom desempenho sob pressão'
    ],
    pontosAtencao: [
      'Podem demonstrar pouca atenção aos detalhes, inclusive aos necessários para viabilizar suas ideias',
      'Tendem a ser impacientes, com risco de abandonar precocemente ideias que não obtenham sucesso imediato',
      'Costumam defender com firmeza seus pontos de vista, podendo mostrar resistência em ouvir outras opiniões',
      'O foco em velocidade e inovação pode comprometer a qualidade dos processos e a eficiência'
    ]
  },
  {
    nome: 'Ancoragem',
    icone: IconeAncoragem,
    perfil: 'Pacientes, estáveis, dispostos a ajudar',
    posicionamento: 'Foco interno, entre a flexibilidade e o controle',
    descricao: 'Cultura estável e confiável, que combina a atenção às pessoas da cultura de Cultivo com o apreço por processos da cultura de Estabilidade. As equipes se dispõem a ajudar umas às outras e compartilham tempo e recursos, envolvendo os interessados na definição de como o trabalho deve ser realizado. O foco está voltado para dentro da organização, nas boas práticas de gestão de pessoas e de processos.',
    pontosFortes: [
      'Buscam equilíbrio entre processos e participação dos colaboradores, envolvendo os stakeholders na definição de como o trabalho deve ser realizado',
      'A sintonia com os acontecimentos internos contribui para a implementação de boas práticas de gestão de pessoas e processos',
      'São participativos, calmos e confiáveis, abertos a opiniões e se esforçam para engajar toda a equipe',
      'Valorizam procedimentos claros e estabelecidos para lidar com conflitos, com o objetivo de minimizar impactos negativos'
    ],
    pontosAtencao: [
      'A priorização de questões internas pode limitar a visão sobre o mercado e as necessidades dos clientes',
      'Há dificuldade em tomar decisões impopulares ou mais arriscadas, mesmo as que afetem a competitividade da organização',
      'O esforço constante para envolver todos pode resultar em perda de eficiência',
      'A busca por consenso e a forte adesão a processos estabelecidos podem reduzir a agilidade'
    ]
  },
  {
    nome: 'Adaptativo',
    icone: IconeAdaptativo,
    perfil: 'Diversos em estilos e comportamentos',
    posicionamento: 'Centro do modelo, sem predominância de um quadrante',
    descricao: 'Cultura marcada pela diversidade de personalidades e estilos de trabalho, sem predominância clara de um dos quadrantes do modelo. Essa variedade permite que a equipe se reorganize conforme o desafio e que sempre haja pessoas adequadas para cada iniciativa. Depende de uma estratégia bem definida para concentrar esforços e evitar a dispersão de recursos.',
    pontosFortes: [
      'A variedade de personalidades e estilos faz com que sempre haja pessoas mais adequadas para cada iniciativa de negócios',
      'Demonstram flexibilidade para se adaptar a diferentes desafios, ajustando suas formas de trabalho ao longo do tempo',
      'A diversidade de estilos favorece a identificação de diferentes perspectivas e contrapontos às ideias',
      'Organizam-se e trabalham em conjunto quando percebem valor em combinar suas forças individuais'
    ],
    pontosAtencao: [
      'Podem ter dificuldade em definir prioridades, seja por questionamentos ou por falta de capacidade de adaptação',
      'Correm o risco de se envolver em muitas iniciativas ao mesmo tempo, comprometendo a execução com excelência',
      'A ausência de uma estratégia bem definida pode levar à dispersão de esforços e ao uso ineficiente de recursos',
      'A parte do time mais aderente ao foco definido tende a ficar sobrecarregada'
    ]
  },
  {
    nome: 'Direcionamento',
    icone: IconeDirecionamento,
    perfil: 'Ágeis, proativos, centrados no cliente e no mercado',
    posicionamento: 'Foco externo, entre a flexibilidade e o controle',
    descricao: 'Cultura incansável e orientada ao mercado, que combina a inovação da cultura de Exploração com a busca por resultados da cultura de Produção. As equipes se mobilizam rapidamente para encantar os clientes atuais e conquistar novos, levando produtos e serviços ao mercado com agilidade. Os conflitos são tratados com naturalidade e o pensamento divergente é bem recebido.',
    pontosFortes: [
      'Têm forte foco no mercado, atuando para encantar clientes atuais e conquistar novos',
      'Buscam surpreender os clientes com produtos e serviços inovadores e de alta qualidade, por meio de agilidade e criatividade',
      'São motivados e se mobilizam rapidamente para alcançar resultados',
      'Lidam com conflitos de forma natural, defendem seus pontos de vista e lidam bem com o pensamento divergente'
    ],
    pontosAtencao: [
      'Podem não dedicar tempo suficiente à coordenação e ao planejamento de recursos, devido à forte orientação para a ação',
      'Pode haver dificuldade em manter a equipe alinhada, pois cada um tende a perseguir suas metas da forma mais rápida possível',
      'Há risco de produzirem resultados concorrentes, pois não privilegiam a cooperação ou o compartilhamento de recursos',
      'Sujeitos a indecisão sobre priorizar recursos na criação de algo novo ou na melhoria dos produtos e serviços existentes'
    ]
  },
  {
    nome: 'Estabilidade',
    icone: IconeEstabilidade,
    perfil: 'Estruturados, concentrados nas tarefas, consistentes',
    posicionamento: 'Controle e foco interno',
    descricao: 'Cultura estruturada, prática e concentrada nas tarefas. Corresponde ao quadrante Hierarquia/Controle do modelo de Cameron e Quinn: a empresa valoriza confiabilidade, eficiência e escalabilidade, em um ambiente formal e estruturado, com lideranças que atuam como coordenadoras e organizadoras. As pessoas seguem procedimentos e regras, e a cadeia de decisão é clara para todos.',
    pontosFortes: [
      'Agem de forma regrada e são cautelosos na gestão de riscos',
      'Buscam atuar com definições claras sobre quem, o que e quando, estruturando o trabalho para evitar conflitos',
      'Valorizam a consistência e a eficiência, corrigindo erros para minimizar a recorrência',
      'Compartilham informações consideradas fundamentais para a condução do trabalho'
    ],
    pontosAtencao: [
      'O foco em sempre "fazer as coisas com a mais alta qualidade e eficiência" pode sufocar a criatividade e a proatividade',
      'Valorizam a precisão, podendo depender muito de regras e processos rígidos, em detrimento da agilidade e da flexibilidade',
      'Podem apresentar resistência ou demora para se adaptar a mudanças',
      'Tendem a compartilhar ideias com reserva, o que pode limitar o fluxo de informações'
    ]
  },
  {
    nome: 'Execução',
    icone: IconeExecucao,
    perfil: 'Disciplinados, conscienciosos, objetivos',
    posicionamento: 'Controle, entre o foco interno e o externo',
    descricao: 'Cultura objetiva e pragmática, que combina o apreço por processos da cultura de Estabilidade com a orientação a resultados da cultura de Produção. O foco está em realizar o trabalho e em encontrar continuamente formas melhores de executá-lo. As pessoas tendem a atuar individualmente em suas tarefas, e os conflitos são tratados de forma lógica, com base em fatos.',
    pontosFortes: [
      'Focam a execução do trabalho e buscam continuamente as melhores formas de realizá-lo',
      'Lidam com conflitos de forma lógica, priorizando os fatos em vez das emoções',
      'Colaboram quando necessário, mas, no geral, preferem atuar de forma individual em suas próprias tarefas',
      'Valorizam a experiência e o conhecimento dos colegas, sendo esse o maior motivador para a troca de recursos e informações'
    ],
    pontosAtencao: [
      'Tendem a alocar poucos recursos em inovação, priorizando os produtos atuais',
      'Podem não se atentar ao impacto do próprio trabalho na estratégia mais ampla do negócio',
      'Existe o risco de não investirem tempo suficiente para se conhecerem e se comunicarem, o que dificulta o alinhamento e a coesão do time',
      'O foco excessivo nos processos e em como o trabalho deve ser feito pode levar ao esgotamento da equipe'
    ]
  },
  {
    nome: 'Produção',
    icone: IconeProducao,
    perfil: 'Competitivos, analíticos, orientados aos resultados',
    posicionamento: 'Controle e foco externo',
    descricao: 'Cultura competitiva, intensa e orientada a resultados. Corresponde ao quadrante Racional/Competitivo do modelo de Cameron e Quinn: a empresa valoriza a reputação, a liderança e o domínio do mercado, em um ambiente orientado a metas, com lideranças competitivas, produtivas e exigentes. Prioriza o alcance de objetivos, a rentabilidade, a capacidade analítica e a execução disciplinada.',
    pontosFortes: [
      'Focados em resultados e, ao mesmo tempo, disciplinados e pautados por fatos e exatidão',
      'Demonstram proatividade e são naturalmente motivados por sua natureza competitiva',
      'O senso de competição impulsiona a manter a organização em um alto padrão de desempenho',
      'Não se abalam pelas barreiras e buscam constantemente excelência e liderança em sua área de atuação'
    ],
    pontosAtencao: [
      'Podem ser controladores e demonstrar uma orientação mais "dura" para obter resultados de determinada maneira',
      'Conflitos podem surgir à medida que os colaboradores competem por recursos para atingir seus próprios objetivos, deixando em segundo plano o alinhamento global',
      'Podem ser muito diretos na comunicação, com pouco interesse em conversas informais, focando em fatos e tarefas',
      'A busca contínua por alta performance e padrões elevados pode gerar pressão excessiva e levar ao esgotamento da equipe'
    ]
  }
]

export const GradeCulturaPrismah: React.FC<iProps> = ({ value, onChange }) => {
  const culturaSelecionada = culturas.find(c => c.nome === value)

  return (
    <Flex flexDir={'column'} gap={'.5rem'} maxW={'48rem'}>
      <Text textAlign={'center'} fontSize={'.75rem'} fontWeight={'700'} color={'var(--c7)'}>Flexibilidade</Text>
      <Grid templateColumns={'repeat(3, 1fr)'} gap={'.5rem'}>
        {culturas.map(c => {
          const selecionada = value === c.nome
          return (
            <Flex
              key={c.nome}
              as={'button'}
              type={'button'}
              alignItems={'center'}
              gap={'.75rem'}
              p={'.75rem 1rem'}
              borderRadius={'.25rem'}
              border={selecionada ? '2px solid var(--Rosa)' : '2px solid var(--c4)'}
              bg={selecionada ? 'var(--RosaOpaco)' : 'transparent'}
              textAlign={'left'}
              onClick={() => onChange(selecionada ? undefined : c.nome)}
            >
              <Flex flexDir={'column'} gap={'.25rem'} flex={1}>
                <Text fontWeight={'700'} fontSize={'.875rem'} color={'black'}>{c.nome}</Text>
                <Text fontSize={'.75rem'} color={'var(--c7)'}>{c.perfil}</Text>
              </Flex>
              <Image src={c.icone} alt={c.nome} w={'2.75rem'} h={'2.75rem'} flexShrink={0}/>
            </Flex>
          )
        })}
      </Grid>
      <Flex justifyContent={'space-between'} fontSize={'.75rem'} fontWeight={'700'} color={'var(--c7)'}>
        <Text>Foco interno</Text>
        <Text>Controle</Text>
        <Text>Foco externo</Text>
      </Flex>

      {culturaSelecionada
        ? (
          <Flex flexDir={'column'} gap={'.75rem'} mt={'.5rem'} p={'1rem'} border={'2px solid var(--c4)'} borderRadius={'.25rem'}>
            <Flex flexDir={'column'}>
              <Text fontWeight={'700'}>{culturaSelecionada.nome}</Text>
              <Text fontSize={'.75rem'} color={'var(--c7)'}>{culturaSelecionada.posicionamento}</Text>
            </Flex>
            <Text fontSize={'.875rem'}>{culturaSelecionada.descricao}</Text>
            <Grid templateColumns={'repeat(2, 1fr)'} gap={'1rem'}>
              <Flex flexDir={'column'} gap={'.25rem'}>
                <Text fontWeight={'700'} fontSize={'.875rem'} color={'var(--Green2)'}>Pontos fortes</Text>
                <UnorderedList fontSize={'.813rem'} spacing={'.25rem'}>
                  {culturaSelecionada.pontosFortes.map(p => <ListItem key={p}>{p}</ListItem>)}
                </UnorderedList>
              </Flex>
              <Flex flexDir={'column'} gap={'.25rem'}>
                <Text fontWeight={'700'} fontSize={'.875rem'} color={'var(--Rosa)'}>Possíveis pontos de atenção</Text>
                <UnorderedList fontSize={'.813rem'} spacing={'.25rem'}>
                  {culturaSelecionada.pontosAtencao.map(p => <ListItem key={p}>{p}</ListItem>)}
                </UnorderedList>
              </Flex>
            </Grid>
          </Flex>
          )
        : <Text fontSize={'.75rem'} color={'var(--c7)'}>Selecione uma cultura para visualizar sua descrição, pontos fortes e possíveis pontos de atenção.</Text>
      }
    </Flex>
  )
}
